/**
 * Core API Response Types
 * 
 * Standardized response wrapper and helper functions for the Dominion Mandate Platform.
 * This module provides the foundation for all API responses across frontend and backend.
 */

import type { PaginationMeta } from './pagination';
import type { ApiError, ApiErrorCode } from './error';

// ============================================================================
// Core Response Types
// ============================================================================

/**
 * Standard API response wrapper
 * 
 * @typeParam T - The type of data being returned
 */
export interface ApiResponse<T = unknown> {
  /** Whether the request was successful */
  success: boolean;
  /** The response data (only present on success) */
  data?: T;
  /** Error details (only present on failure) */
  error?: ApiError;
  /** Response metadata including pagination and timestamp */
  meta?: {
    /** Pagination information */
    pagination?: PaginationMeta;
    /** Response timestamp in ISO 8601 format */
    timestamp: string;
    /** Optional request ID for tracing */
    requestId?: string;
  };
}

/**
 * Type guard to check if a response is successful
 */
export function isSuccessResponse<T>(
  response: ApiResponse<T>
): response is ApiResponse<T> & { success: true; data: T } {
  return response.success === true;
}

/**
 * Type guard to check if a response is an error
 */
export function isErrorResponse(
  response: ApiResponse
): response is ApiResponse & { success: false; error: ApiError } {
  return response.success === false;
}

/**
 * Extract data from a successful response
 */
export function getResponseData<T>(
  response: ApiResponse<T>
): T | undefined {
  return response.success ? response.data : undefined;
}

/**
 * Extract error from a failed response
 */
export function getResponseError(
  response: ApiResponse
): ApiError | undefined {
  return response.success === false ? response.error : undefined;
}

// ============================================================================
// Response Factory Functions
// ============================================================================

/**
 * Create a successful API response
 */
export function createApiResponse<T>(
  data: T,
  meta?: ApiResponse<T>['meta']
): ApiResponse<T> {
  return {
    success: true,
    data,
    meta: {
      ...meta,
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * Create an error API response
 */
export function createErrorApiResponse(
  code: ApiErrorCode,
  message: string,
  details?: Record<string, unknown>
): ApiResponse {
  return {
    success: false,
    error: {
      code,
      message,
      ...(details && { details }),
    },
    meta: {
      timestamp: new Date().toISOString(),
    },
  };
}

// ============================================================================
// HTTP Status Code Mapping
// ============================================================================

/**
 * Map API error codes to HTTP status codes
 */
export function getHttpStatusForErrorCode(code: ApiErrorCode): number {
  const statusMap: Record<ApiErrorCode, number> = {
    // Authentication & Authorization (4xx)
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    TOKEN_EXPIRED: 401,
    INVALID_CREDENTIALS: 401,
    SESSION_EXPIRED: 401,

    // Validation (4xx)
    VALIDATION_ERROR: 400,
    MISSING_REQUIRED_FIELD: 400,
    INVALID_FORMAT: 400,
    INVALID_TOKEN: 401,
    INVALID_INVITE_CODE: 400,

    // Resources (4xx/409)
    NOT_FOUND: 404,
    ALREADY_EXISTS: 409,
    CONFLICT: 409,
    RESOURCE_NOT_FOUND: 404,

    // Operations (4xx)
    CREATE_FAILED: 400,
    UPDATE_FAILED: 400,
    DELETE_FAILED: 400,
    FETCH_FAILED: 400,
    UPLOAD_FAILED: 400,

    // Business Logic (4xx)
    JOIN_FAILED: 400,
    PERMISSION_DENIED: 403,
    STREAM_NOT_LIVE: 400,
    ALREADY_JOINED: 409,
    INVITE_REQUIRED: 403,
    MAX_STREAMS_REACHED: 403,
    STREAM_IN_PROGRESS: 400,
    INVALID_STATE_TRANSITION: 400,
    RATE_LIMIT_EXCEEDED: 429,
    REGION_RESTRICTED: 403,
    USER_BANNED: 403,
    USER_NOT_VERIFIED: 403,

    // Invite-specific
    INVALID_CODE: 400,
    EXPIRED_CODE: 400,
    CODE_LIMIT_EXCEEDED: 400,
    CODE_ALREADY_USED: 409,

    // System (5xx)
    NOT_IMPLEMENTED: 501,
    INTERNAL_ERROR: 500,
    DATABASE_ERROR: 500,
    EXTERNAL_SERVICE_ERROR: 502,
    SERVICE_UNAVAILABLE: 503,
  };

  return statusMap[code] ?? 400;
}

// ============================================================================
// Response Validation
// ============================================================================

/**
 * Validate that an object conforms to ApiResponse structure
 */
export function isApiResponse(obj: unknown): obj is ApiResponse {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const response = obj as Record<string, unknown>;
  return typeof response.success === 'boolean';
}

/**
 * Validate that a response is a success response
 */
export function validateSuccessResponse<T>(
  response: ApiResponse<T>,
  requiredFields?: (keyof T)[]
): response is ApiResponse<T> & { success: true; data: T } {
  if (!isSuccessResponse(response)) {
    return false;
  }

  if (requiredFields && response.data) {
    const data = response.data as Record<string, unknown>;
    return requiredFields.every(field => field in data);
  }

  return true;
}

// ============================================================================
// Type Utilities
// ============================================================================

/**
 * Extract the data type from an ApiResponse
 */
export type ExtractResponseData<T extends ApiResponse> =
  T extends ApiResponse<infer U> ? U : never;

/**
 * Type for API response with required data
 */
export interface ApiResponseWithData<T> extends ApiResponse<T> {
  data: T;
}

/**
 * Type for paginated API response
 */
export interface PaginatedApiResponse<T> extends ApiResponse<T[]> {
  meta: {
    pagination: PaginationMeta;
    timestamp: string;
  };
}

// Re-export error types for convenience
export type { ApiError, ApiErrorCode } from './error';
export type { PaginationMeta, PaginationParams } from './pagination';
